import { format } from 'date-fns';

import type { SiteConfigurationType } from '@dynamika-shared/types/site';

import type {
  BulkSubscriptionRowResult,
  BulkSubscriptionRowStatus,
  BulkSubscriptionUploadResult,
} from '@api/subscriptions';

export const getRowNumbersByStatus = (
  result: BulkSubscriptionUploadResult | undefined,
  status: BulkSubscriptionRowStatus
): number[] =>
  (result?.rowResults ?? [])
    .filter(row => row.status === status)
    .map(row => row.rowNumber)
    .filter((rowNumber): rowNumber is number => typeof rowNumber === 'number')
    .sort((a, b) => a - b);

export const getRejectedRowNumbers = (
  result?: BulkSubscriptionUploadResult
): number[] => getRowNumbersByStatus(result, 'REJECTED');

export const getSkippedRowNumbers = (
  result?: BulkSubscriptionUploadResult
): number[] => getRowNumbersByStatus(result, 'SKIPPED');

export const getRejectedCount = (
  result?: BulkSubscriptionUploadResult
): number => result?.rejectedCount ?? getRejectedRowNumbers(result).length;

export const getSkippedCount = (
  result?: BulkSubscriptionUploadResult
): number => result?.skippedCount ?? getSkippedRowNumbers(result).length;

export const getCreatedCount = (
  result?: BulkSubscriptionUploadResult
): number =>
  result?.createdCount ?? getRowNumbersByStatus(result, 'CREATED').length;

export const isUploadRejected = (
  result?: BulkSubscriptionUploadResult
): boolean =>
  getRejectedCount(result) > 0 || result?.processingComplete === false;

export type UploadSummaryLine = {
  status: BulkSubscriptionRowStatus;
  count: number;
  rowNumbers: number[];
};

export type UploadReportedRow = {
  rowNumber?: number;
  reasons: string[];
};

export type UploadSummary = {
  lines: UploadSummaryLine[];
  rejectedRows: UploadReportedRow[];
  skippedRows: UploadReportedRow[];
  totalRowCount: number;
  isClean: boolean;
  processingComplete: boolean;
  failedAtRowNumber?: number;
  failureReason?: string;
};

const collectRowsByStatus = (
  rows: BulkSubscriptionRowResult[],
  status: BulkSubscriptionRowStatus
): UploadReportedRow[] =>
  rows
    .filter(row => row.status === status)
    .map(row => ({
      rowNumber: row.rowNumber,
      reasons: [...new Set((row.reasons ?? []).filter(Boolean))],
    }))
    .sort((a, b) => (a.rowNumber ?? Infinity) - (b.rowNumber ?? Infinity));

export const buildUploadSummary = (
  result?: BulkSubscriptionUploadResult
): UploadSummary => {
  const created = getCreatedCount(result);
  const skipped = getSkippedCount(result);
  const rejected = getRejectedCount(result);

  const lines: UploadSummaryLine[] = [
    { status: 'CREATED', count: created, rowNumbers: [] },
    {
      status: 'SKIPPED',
      count: skipped,
      rowNumbers: getSkippedRowNumbers(result),
    },
    {
      status: 'REJECTED',
      count: rejected,
      rowNumbers: getRejectedRowNumbers(result),
    },
  ];

  const rowResults = result?.rowResults ?? [];

  return {
    lines,
    rejectedRows: collectRowsByStatus(rowResults, 'REJECTED'),
    skippedRows: collectRowsByStatus(rowResults, 'SKIPPED'),
    totalRowCount: result?.totalRowCount ?? created + skipped + rejected,
    isClean: !isUploadRejected(result),
    processingComplete: result?.processingComplete !== false,
    failedAtRowNumber: result?.failedAtRowNumber,
    failureReason: result?.failureReason,
  };
};

const EXPORT_FILE_NAME_SUBJECT = 'NotificationRecipients';
const EXPORT_FILE_NAME_TIMESTAMP = 'yyyyMMdd-HHmmss';
const ALL_SITES_SEGMENT = 'AllSites';
const UNKNOWN_STUDY_SEGMENT = 'Study';

const toFileNameSegment = (value?: string): string =>
  (value ?? '')
    .trim()
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '');

export type RecipientsExportFileNameArgs = {
  studyCode?: string;
  siteId?: string;
  sites?: SiteConfigurationType[];
  date?: Date;
};

export const buildRecipientsExportFileName = ({
  studyCode,
  siteId,
  sites,
  date = new Date(),
}: RecipientsExportFileNameArgs): string => {
  const study = toFileNameSegment(studyCode) || UNKNOWN_STUDY_SEGMENT;

  const siteCode = sites?.find(site => site.id === siteId)?.code;

  const site = siteId
    ? toFileNameSegment(siteCode) || toFileNameSegment(siteId)
    : ALL_SITES_SEGMENT;

  const timestamp = format(date, EXPORT_FILE_NAME_TIMESTAMP);

  return `${study}_${EXPORT_FILE_NAME_SUBJECT}_${site}_${timestamp}.csv`;
};
